import React from 'react';
import PropTypes from 'prop-types';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCrown } from '@fortawesome/pro-solid-svg-icons';

import CollaboratorHeader from 'components/project/collaborator_header';
import { ProjectDatabaseMemberPropTypes } from 'components/project/prop_types';
import ProjectThumbnail from './project_thumbnail';
import StatusLabel from 'components/status_label';
import Tooltip from 'components/tooltip';

const updatedImageStyles = {
  objectFit: 'cover',
  width: '100%',
  height: '100%'
};

const imageStyle = {
  width: '120px',
  height: '88px'
};

const imageContainerStyle = {
  height: '88px',
  width: '183px',
  overflow: 'hidden'
};

export default function LayoutHeader({
  children,
  collaborators,
  collaboratorsLabel,
  hasDealStatusLabelDisabled,
  premiumListing,
  secondaryActions,
  status,
  subtitle,
  thumbnailUrl,
  title,
  tertiaryTitle,
  useUpdatedStyles = false,
  onCollaboratorsUpdated,
  onUpdateNotificationsBlockedUserIds
}) {
  const tooltipText = 'This property has no sale or lease listing. Use the property edit form to setup a sale or lease \
    listing.';

  const statusLabel = () => {
    // For some reason the StatusLabel.LableWithDot component will not work with the tooltip unless the content is an
    // individual div or icon. We hard coded the child elements that we needed here for now until we can correct this
    // bug.
    if (status === 'No Listing Status') {
      return (
        <Tooltip
          content={tooltipText}
          dark
          placement="bottom"
        >
          <div className="d-inline-block">
            <span className="dot mr-2 label-no-listing-status"></span>
            <span className="label-text-no-listing-status" name="label">No Listing Status</span>
          </div>
        </Tooltip>
      );
    } else {
      return <StatusLabel.LabelWithDot status={status}/>;
    }
  };

  const renderSubTitle = () => {
    if (!subtitle && !status) return;

    const subtitleClass = `text-muted d-inline-block pr-3 mr-3 mb-0 ${
      hasDealStatusLabelDisabled ? '' : 'border-right'
    }`;
    return (
      <div>
        {subtitle && (
          <h4 className={subtitleClass} name="projectSubtitle">
            {subtitle}
          </h4>
        )}
        {status && !hasDealStatusLabelDisabled && (
          <h4 className="d-inline-block mb-0" name="projectDealStatus">
            {statusLabel()}
          </h4>
        )}
      </div>
    );
  };

  return (
    <div className="tab-header layout-header pr-4">
      <div>
        <div className="header-content row-eq-height">
          {useUpdatedStyles ? (
            <ProjectThumbnail
              imageContainerStyle={imageContainerStyle}
              imageStyle={updatedImageStyles}
              noShadow
              thumbnailUrl={thumbnailUrl}
            />
          ) : (
            <ProjectThumbnail
              imageStyle={imageStyle}
              thumbnailUrl={thumbnailUrl}
            />
          )}

          <div className="left-buffer-sm grow">
            <h3 className="mb-0 mt-0">
              {title}
              {premiumListing && <FontAwesomeIcon className="ml-2 text-highlight" icon={faCrown}/>}
            </h3>
            {renderSubTitle()}
            {tertiaryTitle}
          </div>
          <div>
            <div className="row-eq-height">
              <div className="hidden-xs mr-4">
                {collaboratorsLabel &&
                  <h4 className="d-inline-block">{collaboratorsLabel}:</h4>
                }
                <CollaboratorHeader
                  collaborators={collaborators}
                  size="36px"
                  onCollaboratorsUpdated={onCollaboratorsUpdated}
                  onUpdateNotificationsBlockedUserIds={onUpdateNotificationsBlockedUserIds}
                />
              </div>
              {children &&
                <div className="one-line hidden-xs">
                  {children}
                </div>
              }
            </div>
            <div className="text-right">{secondaryActions}</div>
          </div>
        </div>
        <div className="visible-xs bottom-buffer-sm">
          <div className="row-eq-height">
            <div className="grow">
              <CollaboratorHeader
                collaborators={collaborators}
                size="36px"
                onCollaboratorsUpdated={onCollaboratorsUpdated}
                onUpdateNotificationsBlockedUserIds={onUpdateNotificationsBlockedUserIds}
              />
            </div>
            {children &&
              <div className="one-line">
                {children}
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  );
}

LayoutHeader.propTypes = {
  children: PropTypes.node,
  collaborators: PropTypes.arrayOf(ProjectDatabaseMemberPropTypes).isRequired,
  collaboratorsLabel: PropTypes.string,
  hasDealStatusLabelDisabled: PropTypes.bool,
  premiumListing: PropTypes.bool,
  secondaryActions: PropTypes.node,
  status: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  tertiaryTitle: PropTypes.node,
  thumbnailUrl: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  useUpdatedStyles: PropTypes.bool,
  onCollaboratorsUpdated: PropTypes.func,
  onUpdateNotificationsBlockedUserIds: PropTypes.func
};
